/* eslint-disable object-curly-newline */
import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useFormControls } from '../../../common/hooks/useFormControls';

import ArticleValidationField from '../components/ArticleValidationField/ArticleValidationField';
import Button from '../../../common/Button';
import ControlledFormInput from '../../../common/components/ControlledFormInput/ControlledFormInput';
import SubSection from '../../../common/components/SubSection';
import LicensedContentPriceBox from '../components/LicensedContentPriceBox/LicensedContentPriceBox';
import ArticleLicensingTermsField from '../components/ArticleLicensingTermsField/ArticleLicensingTermsField';

import articleLicensingForm, { requiredField } from '../forms/articleLicensingForm';
import ArticleValidationService from '../services/ArticleValidationService';
import styles from './SubmissionScreen.module.scss';

import { licensingURL } from '../constants';

const SubmissionScreen = () => {
  const navigate = useNavigate();
  const { newspost, pushEvent, setFormValues, setNewspost } = useOutletContext();
  const [validLink, setValidLink] = useState(false);
  const [searchParams] = useSearchParams();
  const { site } = window.articleLicensing;
  const {
    fields,
    setError,
    setField,
    validateForm,
    validateField,
    getFormValues,
    resetField,
  } = useFormControls(articleLicensingForm);

  const validationApi = new ArticleValidationService();

  const validateLink = async (newspostUrl) => {
    try {
      const newspostObject = await validationApi.resolveNewspostUrl(newspostUrl);
      setNewspost(newspostObject);
      setValidLink(true);
      return true;
    } catch (error) {
      setError('newspost', error.message);
      setValidLink(false);
      return false;
    }
  };

  const handleLinkReset = () => {
    setNewspost({});
    setValidLink(false);
    resetField('newspost');
  };

  const handleNewspostUrlBlur = (e) => {
    if (e.target.value) validateLink(e.target.value); else validateField(e);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fields.newspost.value) {
      validateLink(fields.newspost.value);
    }
    if (validateForm() && validLink) {
      const data = getFormValues();
      data.newspost = newspost?.id;
      setFormValues(data);
      pushEvent('pageOneSubmit', { nl_subscribe: !!data.subscribe });
      navigate(`${licensingURL}/payment`);
    }
  };

  useEffect(() => {
    const newspostUrl = searchParams.get('newspostUrl');
    if (newspostUrl) {
      validateLink(newspostUrl).then((isValid) => {
        if (isValid) {
          setField({ target: { name: 'newspost', value: newspostUrl } });
        }
      });
    }
  }, [searchParams]);

  return (
    <div className="row collapse">
      <div className="row">
        <div className="large-4 large-push-8 column">
          <LicensedContentPriceBox />
        </div>
        <div className="large-8 large-pull-4 column">
          <SubSection title="Order information">
            <form id="article-licensing-form" className={styles.form} onSubmit={handleSubmit}>
              {Object.entries(fields).map(([fieldName, field]) => {
                switch (fieldName) {
                  case 'newspost':
                    return (
                      <ArticleValidationField
                        key={fieldName}
                        field={field}
                        fieldName={fieldName}
                        newspost={newspost}
                        onBlur={handleNewspostUrlBlur}
                        onChange={setField}
                        resetLink={handleLinkReset}
                        required={field.validators.includes(requiredField)}
                        validLink={validLink}
                      />
                    );
                  case 'subscribe':
                    return (
                      <ArticleLicensingTermsField
                        key={fieldName}
                        field={{ ...field, name: fieldName }}
                        onChange={setField}
                        labelContent={`Subscribe to the ${site.name} daily newsletter`}
                        descriptionContent={(
                          <>
                            By signing up to receive our newsletter, you agree to our{' '}
                            <a
                              className={styles.policyLink}
                              href="https://www.techtarget.com/terms-of-use/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Terms of Use
                            </a>{' '}
                            and{' '}
                            <a
                              className={styles.policyLink}
                              href="https://www.techtarget.com/privacy-policy/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Privacy Policy
                            </a>
                            . You can unsubscribe at anytime.
                          </>
                        )}
                      />
                    );
                  default:
                    return (
                      <ControlledFormInput
                        key={fieldName}
                        field={field}
                        fieldName={fieldName}
                        onBlur={validateField}
                        onChange={setField}
                        required={field.validators.includes(requiredField)}
                      />
                    );
                }
              })}
              <Button as="button" type="submit">
                Continue to checkout
              </Button>
            </form>
          </SubSection>
        </div>
      </div>
    </div>
  );
};

export default SubmissionScreen;
